/**
 * Email-thread (ahoy_email_thread) CRM trigger card variant — Header,
 * collapsed PreviewBody, and expanded Body, plus the recipient row and
 * provider-link button the expanded body composes.
 */

import { useCallback, useMemo } from 'react';

import {
  EntityBadge,
  type EntityBadgeState,
} from '@/lib/crm/components/badges';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { GmailIcon } from '@/lib/crm/utils/icons/CustomIcons';
import { EmailIframe } from '@/lib/email';
import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { type TimeFormat, timeParts, useOrgTimeFormat } from '@/lib/time';

import {
  decodeHtmlEntities,
  extractSenderEmail,
  extractSenderName,
} from '../shared';
import { getStringArrayValue, getStringValue } from './helpers';

interface EmailParticipantEntity {
  entity_id: string;
  entity_type_name: string;
  display_name: string;
}

/** Badge for a single email participant entity — extracted to allow stable useCallback. */
function EmailEntityBadge({ entity }: { entity: EmailParticipantEntity }) {
  const openSlideout = useOpenSlideoutOptional();

  const handleClick = useCallback(() => {
    openSlideout?.(entity.entity_id);
  }, [openSlideout, entity.entity_id]);

  const state = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: {
        id: entity.entity_id,
        entityType: entity.entity_type_name,
        displayName: entity.display_name,
      },
    }),
    [entity.entity_id, entity.entity_type_name, entity.display_name]
  );

  return (
    <EntityBadge
      state={state}
      variant='inline'
      onClick={openSlideout ? handleClick : undefined}
    />
  );
}

// Reused by every keyboard-activated button in the card so Enter/Space
// don't bubble up to the wrapping `role='button'` toggle.
function stopEnterAndSpacePropagation(
  e: React.KeyboardEvent<HTMLButtonElement>
) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.stopPropagation();
  }
}

/**
 * EmailProviderButton — opens an email thread in the provider's web UI.
 * For Gmail threads, links to mail.google.com. For Outlook threads, shows
 * the Outlook icon but doesn't link out (Outlook Web doesn't support a
 * simple conversation-ID URL pattern).
 */
export function EmailProviderButton({
  threadId,
  provider,
}: {
  threadId: string;
  provider: string | null;
}) {
  const isOutlook = provider === 'outlook';
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutlook) {
        window.open(
          `https://mail.google.com/mail/u/0/#all/${threadId}`,
          '_blank',
          'noopener,noreferrer'
        );
      }
    },
    [threadId, isOutlook]
  );

  if (isOutlook) {
    return (
      <span
        className='p-1 rounded inline-flex items-center'
        aria-label='Outlook thread'
      >
        <img
          src='/assets/figma/outlook-icon.svg'
          alt='Outlook'
          className='w-4 h-4'
        />
      </span>
    );
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      onKeyDown={stopEnterAndSpacePropagation}
      className='p-1 rounded hover:bg-tertiary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500'
      aria-label='Open in Gmail'
    >
      <GmailIcon className='w-4 h-4' />
    </button>
  );
}

/**
 * Email thread preview component for ahoy_email_thread entities
 */
function EmailRecipientRow({
  label,
  addresses,
  participantEntities,
}: {
  label: string;
  addresses: string[];
  participantEntities?: Record<string, EmailParticipantEntity>;
  orgId?: number;
}) {
  if (addresses.length === 0) {
    return null;
  }
  return (
    <div className='flex items-baseline gap-1.5'>
      <span className='text-sm text-tertiary flex-shrink-0 min-w-10'>
        {label}:
      </span>
      <div className='flex flex-wrap gap-1 items-baseline'>
        {addresses.map(addr => {
          const email = extractSenderEmail(addr).toLowerCase();
          const entity = participantEntities?.[email];
          if (entity) {
            return <EmailEntityBadge key={addr} entity={entity} />;
          }
          return (
            <span key={addr} className='text-sm text-secondary'>
              {addr}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function getEmailThreadData(data: CrmEventTriggerData, timeFormat: TimeFormat) {
  const { event, display_name, participant_entities } = data.crm_event;
  const from = getStringValue(event, 'from') || display_name;
  const senderName = from ? extractSenderName(from) : 'Unknown sender';
  const subject = getStringValue(event, 'subject') || display_name;
  const snippet = getStringValue(event, 'snippet') || '';
  const date = getStringValue(event, 'date');
  const formattedDateTime = date
    ? (() => {
        const d = new Date(date);
        if (isNaN(d.getTime())) {
          return null;
        }
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          // Shared time options — midnight-safe hour cycle, org-aware format.
          ...timeParts(timeFormat),
        });
      })()
    : null;
  const bodyText = getStringValue(event, 'body_text');
  const bodyHtml = getStringValue(event, 'body_html');
  const toRecipients = getStringArrayValue(event, 'to');
  const ccRecipients = getStringArrayValue(event, 'cc');
  const bccRecipients = getStringArrayValue(event, 'bcc');
  return {
    from,
    senderName,
    subject,
    snippet,
    formattedDateTime,
    bodyText,
    bodyHtml,
    toRecipients,
    ccRecipients,
    bccRecipients,
    participantEntities: participant_entities,
  };
}

export function EmailThreadHeader({ data }: { data: CrmEventTriggerData }) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { senderName, subject } = getEmailThreadData(data, timeFormat);
  // Time intentionally rendered alongside the side buttons in the parent
  // CrmEventTriggerPreview so the [time, Gmail, Open in CRM, chevron]
  // group reads as one tight cluster on the trigger's right edge —
  // matches the original hand-rolled layout.
  return (
    <div className='flex-1 min-w-0'>
      <div className='flex items-center gap-1.5 min-w-0'>
        <span className='text-sm font-medium text-primary flex-shrink-0'>
          {senderName}
        </span>
        <span className='text-tertiary flex-shrink-0'>·</span>
        <span className='text-sm text-secondary truncate'>{subject}</span>
      </div>
    </div>
  );
}

/** Compact email-thread preview rendered into AccordionItem.previewContent
 *  while collapsed — the snippet line-clamp the original showed. Animates
 *  closed (1fr → 0fr) when the user expands the disclosure, while
 *  EmailThreadBody animates open (0fr → 1fr) over the same window. */
export function EmailThreadPreviewBody({
  data,
}: {
  data: CrmEventTriggerData;
}) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { snippet } = getEmailThreadData(data, timeFormat);
  if (!snippet) {
    return null;
  }
  // `truncate` clips the snippet to a single line with the browser's
  // CSS `text-overflow: ellipsis` when it overflows. We also append a
  // literal `…` so the affordance is visible even when the snippet
  // happens to fit — the snippet is the first ~140 chars of the email,
  // so there's almost always more content waiting in the expanded view.
  // Concatenated into one text node so test queries that match the raw
  // snippet (e.g. `getByText('Thanks for reaching out!')`) still find
  // it — JSX would otherwise split text + `…` into two text nodes.
  const previewText = `${decodeHtmlEntities(snippet)}…`;
  return <div className='truncate text-xs text-tertiary'>{previewText}</div>;
}

export function EmailThreadBody({
  data,
  orgId,
}: {
  data: CrmEventTriggerData;
  orgId?: number;
}) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const {
    from,
    snippet,
    bodyText,
    bodyHtml,
    toRecipients,
    ccRecipients,
    bccRecipients,
    participantEntities,
  } = getEmailThreadData(data, timeFormat);
  return (
    <div className='space-y-2'>
      <div className='space-y-1.5'>
        <EmailRecipientRow
          label='From'
          addresses={from ? [from] : []}
          participantEntities={participantEntities}
          orgId={orgId}
        />
        <EmailRecipientRow
          label='To'
          addresses={toRecipients}
          participantEntities={participantEntities}
          orgId={orgId}
        />
        <EmailRecipientRow
          label='CC'
          addresses={ccRecipients}
          participantEntities={participantEntities}
          orgId={orgId}
        />
        <EmailRecipientRow
          label='BCC'
          addresses={bccRecipients}
          participantEntities={participantEntities}
          orgId={orgId}
        />
      </div>

      {bodyHtml && bodyHtml.trim() ? (
        <EmailIframe html={bodyHtml} minHeight='60px' className='w-full' />
      ) : (
        <div className='text-sm text-secondary whitespace-pre-wrap'>
          {bodyText || decodeHtmlEntities(snippet)}
        </div>
      )}
    </div>
  );
}
